import authService from './authService';

const API_BASE = '/api/admin/logs';

class AccessLogsService {
  async getLoginHistory(filters = {}) {
    const token = authService.getToken();
    const params = new URLSearchParams();
    
    if (filters.userId) params.append('userId', filters.userId);
    if (filters.email) params.append('email', filters.email);
    if (filters.ipAddress) params.append('ipAddress', filters.ipAddress);
    if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
    if (filters.scopeType) params.append('scopeType', filters.scopeType);
    if (filters.scopeId) params.append('scopeId', filters.scopeId);
    params.append('page', filters.page || 1);
    params.append('pageSize', filters.pageSize || 50);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await fetch(`${API_BASE}/login?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch login history: ${response.statusText}`);
    }

    return await response.json();
  }

  async getFailedAttempts(filters = {}) {
    const token = authService.getToken();
    const params = new URLSearchParams();
    
    if (filters.email) params.append('email', filters.email);
    if (filters.ipAddress) params.append('ipAddress', filters.ipAddress);
    if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
    if (filters.scopeType) params.append('scopeType', filters.scopeType);
    if (filters.scopeId) params.append('scopeId', filters.scopeId);
    params.append('page', filters.page || 1);
    params.append('pageSize', filters.pageSize || 50);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await fetch(`${API_BASE}/failed-attempts?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch failed attempts: ${response.statusText}`);
    }

    return await response.json();
  }

  async getPermissionChanges(filters = {}) {
    const token = authService.getToken();
    const params = new URLSearchParams();
    
    if (filters.adminId) params.append('adminId', filters.adminId);
    if (filters.affectedUserId) params.append('affectedUserId', filters.affectedUserId);
    if (filters.changeType) params.append('changeType', filters.changeType);
    if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
    if (filters.scopeType) params.append('scopeType', filters.scopeType);
    if (filters.scopeId) params.append('scopeId', filters.scopeId);
    params.append('page', filters.page || 1);
    params.append('pageSize', filters.pageSize || 50);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await fetch(`${API_BASE}/permission-changes?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch permission changes: ${response.statusText}`);
    }

    return await response.json();
  }

  async getUserTimeline(userId, filters = {}) {
    const token = authService.getToken();
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
    if (filters.activityType) params.append('activityType', filters.activityType);
    params.append('page', filters.page || 1);
    params.append('pageSize', filters.pageSize || 50);

    // URL-encode the userId to handle emails with @ symbol and other special characters
    const encodedUserId = encodeURIComponent(userId);

    const response = await fetch(`${API_BASE}/user-timeline/${encodedUserId}?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      // Try to get a more detailed error message from the response
      let errorMessage = `Failed to fetch user timeline: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch {
        // If response isn't JSON, use the default message
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  }

  async getSummary(filters = {}) {
    const token = authService.getToken();
    const params = new URLSearchParams();
    
    if (filters.startDate) params.append('startDate', filters.startDate.toISOString());
    if (filters.endDate) params.append('endDate', filters.endDate.toISOString());
    if (filters.scopeType) params.append('scopeType', filters.scopeType);
    if (filters.scopeId) params.append('scopeId', filters.scopeId);

    const response = await fetch(`${API_BASE}/summary?${params}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch summary: ${response.statusText}`);
    }

    return await response.json();
  }

  async blockIP(ipAddress, reason, expiresAt) {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE}/block-ip`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ ipAddress, reason, expiresAt })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to block IP address');
    }

    return await response.json();
  }

  async unblockIP(blockId) {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE}/block-ip/${blockId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Failed to unblock IP address');
    }

    return await response.json();
  }

  async getBlockedIPs() {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE}/blocked-ips`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch blocked IPs');
    }

    return await response.json();
  }

  async createExport(request) {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE}/export`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(request)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to create export');
    }

    return await response.json();
  }

  async getExports(page = 1, pageSize = 20) {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE}/exports?page=${page}&pageSize=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch exports');
    }

    return await response.json();
  }

  async downloadExport(exportId) {
    const token = authService.getToken();
    const response = await fetch(`${API_BASE}/export/${exportId}/download`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to download export');
    }

    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `export_${exportId}.${response.headers.get('Content-Type')?.includes('csv') ? 'csv' : response.headers.get('Content-Type')?.includes('json') ? 'json' : 'pdf'}`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export default new AccessLogsService();

